const homepageCleanups = new Map();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initHomepageSections(), { once: true });
} else {
  initHomepageSections();
}

document.addEventListener('shopify:section:load', (event) => {
  initHomepageSections(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  for (const [element, cleanup] of homepageCleanups) {
    if (event.target.contains(element)) {
      cleanup();
      homepageCleanups.delete(element);
    }
  }
});

function initHomepageSections(root = document) {
  // Section rendering can replace nodes without an unload event.
  for (const [element, cleanup] of homepageCleanups) {
    if (!element.isConnected) {
      cleanup();
      homepageCleanups.delete(element);
    }
  }

  const hero = root.matches?.('.hero') ? root : root.querySelector('.hero');
  setupHeroSlider(hero);
  const rails = root.matches?.('[data-product-rail]') ? [root] : root.querySelectorAll('[data-product-rail]');
  rails.forEach((rail) => {
    setupPager({
      gridId: rail.dataset.gridId,
      tabsId: rail.dataset.tabsId,
      prevId: rail.dataset.prevId,
      nextId: rail.dataset.nextId,
      counterId: rail.dataset.counterId,
    });
  });
}

function setupHeroSlider(hero) {
  if (!hero) return;
  if (hero.dataset.sliderReady === 'true') return;

  const slides = Array.from(hero.querySelectorAll('.hero-slide'));
  const dotsEl = hero.querySelector('#heroDots');
  const numEl = hero.querySelector('#heroNum');
  const totalEl = hero.querySelector('#heroTotal');
  const headlineEl = hero.querySelector('#heroHeadline');
  const ledeEl = hero.querySelector('#heroLede');
  const cta1 = hero.querySelector('#heroCta1');
  const cta2 = hero.querySelector('#heroCta2');
  const prev = hero.querySelector('#heroPrev');
  const next = hero.querySelector('#heroNext');
  const playback = hero.querySelector('#heroPlayback');

  if (!slides.length || !dotsEl || !numEl || !totalEl || !headlineEl || !ledeEl || !cta1 || !cta2) {
    return;
  }
  hero.dataset.sliderReady = 'true';

  const autoplayEnabled = hero.dataset.autoplay !== 'false';
  const intervalMs = parseInt(hero.dataset.interval || '6000', 10);
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const videos = Array.from(hero.querySelectorAll('video'));
  const events = new AbortController();
  const on = (target, type, listener) => target?.addEventListener(type, listener, { signal: events.signal });
  let current = 0;
  let timer;
  let userPaused = motion.matches;
  let inView = false;
  let hovered = false;
  let focused = false;
  let editorSelected = false;

  if (playback) playback.hidden = !(videos.length || (autoplayEnabled && slides.length > 1));

  totalEl.textContent = String(slides.length).padStart(2, '0');
  dotsEl.innerHTML = slides
    .map((_, index) => `<button type="button" data-i="${index}" aria-label="Go to slide ${index + 1}" ${index === 0 ? 'aria-current="true"' : ''}></button>`)
    .join('');

  const dots = Array.from(dotsEl.querySelectorAll('button'));

  function applyButton(button, slide, prefix) {
    const label = slide.dataset[`${prefix}Label`] || slide.dataset[prefix] || '';
    const href = slide.dataset[`${prefix}Href`] || '';
    const style = slide.dataset[`${prefix}Style`] || '';
    const textSpan = button.querySelector('span');
    const isHidden = !label || !href;

    if (textSpan) textSpan.textContent = label;
    if (isHidden) {
      button.removeAttribute('href');
    } else {
      button.href = href;
    }
    button.hidden = isHidden;
    button.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
    button.classList.remove('button', 'button-secondary', 'button--light', 'button--outline-light', 'btn-on-color', 'btn-on-color-outline', 'btn-primary', 'btn-secondary');
    if (style === 'btn-on-color' || style === 'on_color_filled') {
      button.classList.add('button', 'button--light');
    } else if (style === 'btn-on-color-outline' || style === 'on_color_outline') {
      button.classList.add('button-secondary', 'button--outline-light');
    } else if (style === 'btn-secondary' || style === 'secondary' || style === 'outline') {
      button.classList.add('button-secondary');
    } else {
      button.classList.add('button');
    }
  }

  function goTo(index) {
    current = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('active', slideIndex === current);
      slide.setAttribute('aria-hidden', slideIndex === current ? 'false' : 'true');
    });
    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('active', dotIndex === current);
      dot.setAttribute('aria-current', dotIndex === current ? 'true' : 'false');
    });

    const activeSlide = slides[current];
    headlineEl.innerHTML = activeSlide.dataset.headline || '';
    ledeEl.textContent = activeSlide.dataset.lede || '';
    numEl.textContent = String(current + 1).padStart(2, '0');
    applyButton(cta1, activeSlide, 'cta1');
    applyButton(cta2, activeSlide, 'cta2');
    syncPlayback();
  }

  function syncPlayback() {
    window.clearTimeout(timer);
    const canPlay = !userPaused && inView && !document.hidden;
    videos.forEach((video) => {
      if (!canPlay || !slides[current].contains(video)) {
        video.pause();
        return;
      }
      // Inactive slides do not fetch video sources until they are first shown.
      const sources = video.querySelectorAll('source[data-src]');
      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      if (sources.length) video.load();
      if (video.paused) video.play().catch(() => {});
    });
    if (playback) {
      const label = userPaused ? 'Resume hero animation' : 'Pause hero animation';
      playback.dataset.paused = String(userPaused);
      playback.setAttribute('aria-label', label);
      playback.title = label;
    }
    if (canPlay && autoplayEnabled && slides.length > 1 && !hovered && !focused && !editorSelected) {
      timer = window.setTimeout(() => goTo(current + 1), Number.isNaN(intervalMs) ? 6000 : intervalMs);
    }
  }

  function handleEditorBlockSelect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !hero.contains(target)) return;
    const selectedSlide = target.closest('.hero-slide');
    if (!selectedSlide) return;
    const selectedIndex = slides.indexOf(selectedSlide);
    if (selectedIndex < 0) return;
    editorSelected = true;
    goTo(selectedIndex);
  }

  function handleEditorBlockDeselect(event) {
    const target = event.target;
    if (!(target instanceof Element) || !hero.contains(target)) return;
    editorSelected = false;
    syncPlayback();
  }

  on(prev, 'click', () => {
    goTo(current - 1);
  });

  on(next, 'click', () => {
    goTo(current + 1);
  });

  on(dotsEl, 'click', (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    const index = parseInt(button.dataset.i || '0', 10);
    goTo(index);
  });

  on(playback, 'click', () => {
    userPaused = !userPaused;
    syncPlayback();
  });
  on(hero, 'mouseenter', () => { hovered = true; syncPlayback(); });
  on(hero, 'mouseleave', () => { hovered = false; syncPlayback(); });
  on(hero, 'focusin', () => { focused = true; syncPlayback(); });
  on(hero, 'focusout', (event) => {
    focused = hero.contains(event.relatedTarget);
    syncPlayback();
  });
  on(motion, 'change', () => {
    if (motion.matches) userPaused = true;
    syncPlayback();
  });
  on(document, 'visibilitychange', syncPlayback);

  on(document, 'shopify:block:select', handleEditorBlockSelect);
  on(document, 'shopify:block:deselect', handleEditorBlockDeselect);

  const observer = new IntersectionObserver(([entry]) => {
    inView = entry.isIntersecting;
    syncPlayback();
  });
  observer.observe(hero);
  homepageCleanups.set(hero, () => {
    window.clearTimeout(timer);
    observer.disconnect();
    events.abort();
    videos.forEach((video) => video.pause());
    delete hero.dataset.sliderReady;
  });

  goTo(0);
}

function setupPager({ gridId, tabsId, prevId, nextId, counterId }) {
  const grid = document.getElementById(gridId);
  if (!grid) return;
  if (grid.dataset.pagerReady === 'true') return;
  grid.dataset.pagerReady = 'true';

  const cards = Array.from(grid.children);
  const prev = document.getElementById(prevId);
  const next = document.getElementById(nextId);
  const counter = document.getElementById(counterId);
  const tabs = tabsId ? Array.from(document.querySelectorAll(`#${tabsId} button`)) : [];
  let page = 0;
  let activeCategory = 'all';
  let lastPerPage = 0;
  const events = new AbortController();

  function getPerPage() {
    // Computed tracks reflect the final CSS, including section overrides.
    const tracks = window.getComputedStyle(grid).gridTemplateColumns;
    return tracks && tracks !== 'none' ? tracks.trim().split(/\s+/).length : 1;
  }

  function filteredCards() {
    if (activeCategory === 'all') return cards;
    return cards.filter((card) => (card.dataset.cat || '').toLowerCase() === activeCategory);
  }

  function render() {
    const perPage = getPerPage();
    lastPerPage = perPage;
    const visibleCards = filteredCards();
    const totalPages = Math.max(1, Math.ceil(visibleCards.length / perPage));
    page = ((page % totalPages) + totalPages) % totalPages;
    const start = page * perPage;
    const end = start + perPage;

    cards.forEach((card) => {
      card.hidden = true;
      card.style.display = 'none';
    });

    visibleCards.slice(start, end).forEach((card) => {
      card.hidden = false;
      card.style.display = '';
    });

    if (counter) {
      counter.textContent = `${page + 1} / ${totalPages}`;
    }

    if (prev) prev.disabled = totalPages <= 1;
    if (next) next.disabled = totalPages <= 1;
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      activeCategory = (tab.dataset.cat || 'all').toLowerCase();
      page = 0;
      render();
    }, { signal: events.signal });
  });

  prev?.addEventListener('click', () => {
    page -= 1;
    render();
  }, { signal: events.signal });

  next?.addEventListener('click', () => {
    page += 1;
    render();
  }, { signal: events.signal });

  const observer = new ResizeObserver(() => {
    const nextPerPage = getPerPage();
    if (nextPerPage !== lastPerPage) {
      page = Math.floor(page * lastPerPage / nextPerPage);
      render();
    }
  });
  observer.observe(grid);
  homepageCleanups.set(grid, () => {
    observer.disconnect();
    events.abort();
    delete grid.dataset.pagerReady;
  });

  if (tabs.length) {
    const defaultTab = tabs.find((tab) => tab.classList.contains('active')) || tabs[0];
    activeCategory = (defaultTab?.dataset.cat || 'all').toLowerCase();
    tabs.forEach((tab) => tab.setAttribute('aria-selected', tab === defaultTab ? 'true' : 'false'));
  }

  lastPerPage = getPerPage();
  render();
}
